import React, { useMemo } from "react";
import { Card, Empty, Spin } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { useGetCommentsByTable } from "service/comment.service";
import { isDpatQualityAssuranceCommentRole } from "../utils/dpatCommentRoleLabel";
import { formatDpatSlugAsCodeLabel } from "../utils/dpatIndicatorCmsHelpers";
import { DPAT_SLUG_TO_TABLE_KEY, slugFromTableCommentedId } from "../utils/dpatManualScoreHelpers";
import { QA_MEMO_TABLE_ID } from "../utils/dpatQaMemoTemplate";

const SLUG_ORDER = Object.keys(DPAT_SLUG_TO_TABLE_KEY);

function commentTextFromRow(comment) {
  return (comment?.comments != null && comment.comments !== "" ? comment.comments : comment?.comment) ?? "";
}

function indicatorLabel(tableCommented) {
  const slug = slugFromTableCommentedId(tableCommented);
  return formatDpatSlugAsCodeLabel(slug) || tableCommented || "—";
}

function slugSortIndex(tableCommented) {
  const slug = slugFromTableCommentedId(tableCommented);
  const index = SLUG_ORDER.indexOf(slug);
  return index === -1 ? SLUG_ORDER.length : index;
}

const QualityAssuranceCommentsSummary = ({ district, year, enabled = true }) => {
  const { data: response, isLoading, isError } = useGetCommentsByTable(
    district?.value,
    year,
    "DPAT",
    { enabled: enabled !== false },
  );

  const qaComments = useMemo(() => {
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];

    return rows
      .filter((comment) => {
        if (!isDpatQualityAssuranceCommentRole(comment?.userRole)) return false;
        if (String(comment?.tableCommented || "") === QA_MEMO_TABLE_ID) return false;
        return Boolean(String(commentTextFromRow(comment)).trim());
      })
      .sort((a, b) => {
        const bySlug = slugSortIndex(a.tableCommented) - slugSortIndex(b.tableCommented);
        if (bySlug !== 0) return bySlug;
        return String(a.tableCommented || "").localeCompare(String(b.tableCommented || ""));
      });
  }, [response]);

  if (isLoading) {
    return (
      <Card>
        <div className="text-center py-12">
          <Spin size="large" tip="Loading Quality Assurance comments..." />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <Empty description="Could not load Quality Assurance comments." />
      </Card>
    );
  }

  if (qaComments.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className="text-center">
              <FileTextOutlined className="text-4xl text-muted-foreground mb-2" />
              <p>No Quality Assurance comments available</p>
              <p className="text-xs text-muted-foreground mt-1">
                No QA comments found for {district?.label} in {year}
              </p>
            </div>
          }
        />
      </Card>
    );
  }

  return (
    <div className="table-responsive">
      <table
        className="table table-bordered"
        style={{
          border: "1px solid #000",
          borderCollapse: "collapse",
          width: "100%",
          marginTop: "20px",
        }}
      >
        <thead>
          <tr style={{ fontWeight: "bold", border: "1px solid #000" }}>
            <th style={{ border: "1px solid #000", fontWeight: "bold", padding: "8px" }}>No.</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold", padding: "8px" }}>Indicator</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold", padding: "8px" }}>
              Quality Assurance Comments
            </th>
          </tr>
        </thead>
        <tbody>
          {qaComments.map((item, index) => (
            <tr key={item.id || `${item.tableCommented}-${index}`}>
              <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center" }}>
                <strong>{index + 1}</strong>
              </td>
              <td style={{ border: "1px solid #000", padding: "8px" }}>
                <strong>{indicatorLabel(item.tableCommented)}</strong>
              </td>
              <td style={{ border: "1px solid #000", padding: "8px", whiteSpace: "pre-wrap" }}>
                {commentTextFromRow(item)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default QualityAssuranceCommentsSummary;
